import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, Card, CardContent, Grid, CircularProgress,
  Chip, IconButton, Tooltip, TextField, Select, MenuItem, InputLabel, FormControl,
  Alert, Collapse, Stack, Tabs, Tab
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Assignment as CasesIcon,
  Schedule as PendingIcon,
  CheckCircle as CompletedIcon,
  Edit as EditIcon, 
  Visibility as ViewIcon,
  Clear as ClearIcon,
  ExpandLess,
  ExpandMore,
  FilterList as FilterIcon,
  Drafts as DraftIcon,
  Timelapse as InProgressIcon,
  FactCheck as EvidenceIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types/auth';

import CaptureWizardModal from '../../components/modals/CaptureWizardModal';
import { usePhoneCallService } from '../../services/phoneCallService';
import type { AgentDashboardStats, PhoneCall, PhoneCallAgentScope } from '../../types/phoneCapture';
import SeverityLevelBadge from '../../components/common/SeverityLevelBadge';
import { collectIntakeServiceEntries, isIntakeServiceChannelized } from '../../utils/phoneCallClaims';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'ASSIGNED': return 'info';
    case 'IN_PROGRESS': return 'warning';
    case 'CAPTURED': return 'info';
    case 'DRAFT': return 'default';
    case 'PENDING': return 'default';
    default: return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'COMPLETED': return 'Completado';
    case 'ASSIGNED': return 'Asignado';
    case 'IN_PROGRESS': return 'En Progreso';
    case 'CAPTURED': return 'Capturado';
    case 'DRAFT': return 'Borrador';
    case 'PENDING': return 'Pendiente';
    default: return status;
  }
};

const formatPrivateBeneficiaryName = (row: Record<string, unknown>): string => {
  const parts = [row.privateFirstName, row.privateLastName, row.privateMiddleName]
    .map((x) => String(x ?? '').trim())
    .filter(Boolean);
  return parts.join(' ').trim() || 'Sin nombre';
};

/** Resumen para tabla: varios beneficiarios y servicios (referrals) vs. legado un solo `patient`. */
const summarizeIntakeForTable = (call: PhoneCall) => {
  const callRecord = call as unknown as Record<string, unknown>;
  const entries = collectIntakeServiceEntries(callRecord);

  if (entries.length > 0) {
    const serviceCount = entries.length;
    const assignedCount = entries.filter((entry) => isIntakeServiceChannelized(entry)).length;

    const clients = Array.from(
      new Map(
        entries.map((entry, index) => {
          const key =
            entry.client.id != null && String(entry.client.id).trim() !== ''
              ? String(entry.client.id)
              : `row-${index}`;
          return [key, entry.client] as const;
        })
      ).values()
    );

    const ben = clients.length;
    const first = ben > 0 ? formatPrivateBeneficiaryName(clients[0]) : 'Sin nombre';
    const beneficiariesLabel = `${ben} beneficiario${ben === 1 ? '' : 's'} · ${serviceCount} servicio${serviceCount === 1 ? '' : 's'}`;
    const hintLine = ben === 1 ? first : `${first} (+${ben - 1})`;

    let channelLine: string;
    if (serviceCount === 0) channelLine = '—';
    else if (assignedCount === 0) channelLine = `${serviceCount} sin canalizar`;
    else if (assignedCount === serviceCount) channelLine = `Canalizado (${assignedCount})`;
    else channelLine = `${assignedCount}/${serviceCount} canalizados`;

    return { beneficiariesLabel, hintLine, channelLine };
  }

  return {
    beneficiariesLabel: 'Sin servicios',
    hintLine: '—',
    channelLine: '—'
  };
};

const TABLE_DASH = '—';

/** Instante en que el registro quedó guardado en el sistema (prioridad: creación, luego última actualización). */
const resolveCaptureInstantIso = (call: PhoneCall): string | undefined => {
  const c = call.createdAt?.trim();
  if (c) return c;
  const u = call.updatedAt?.trim();
  return u || undefined;
};

const formatTimeEsMx = (iso?: string | null): string => {
  if (iso == null || String(iso).trim() === '') return TABLE_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return TABLE_DASH;
  return d.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatDurationForTable = (duration?: string | null): string => {
  const s = duration != null ? String(duration).trim() : '';
  return s !== '' ? s : TABLE_DASH;
};

const SCOPE_TAB_META: Record<
  PhoneCallAgentScope,
  { label: string; emptyMessage: string }
> = {
  created: {
    label: 'Mis intakes',
    emptyMessage: 'No has registrado intakes con los filtros actuales.',
  },
  assigned: {
    label: 'Para seguimiento',
    emptyMessage: 'No tienes casos asignados para seguimiento con los filtros actuales.',
  },
  all: {
    label: 'Todas',
    emptyMessage: 'No se encontraron llamadas con los filtros actuales.',
  },
};

const PhoneAgentDashboard: React.FC = () => {
  const { user, hasAnyRole } = useAuth();
  const [captureWizardModalOpen, setCaptureWizardModalOpen] = useState(false);
  const [wizardInitialCallId, setWizardInitialCallId] = useState<number | null>(null);
  const [wizardReadOnly, setWizardReadOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados para filtros (según FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md - búsqueda por texto)
  const [listScope, setListScope] = useState<PhoneCallAgentScope>('created');
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
  const [patientNameFilter, setPatientNameFilter] = useState<string>('');
  const [companyNameFilter, setCompanyNameFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [filterError, setFilterError] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);

  // Estados para datos reales
  const [phoneCalls, setPhoneCalls] = useState<PhoneCall[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<AgentDashboardStats>({
    totalCalls: 0,
    draft: 0,
    captured: 0,
    assigned: 0,
    inProgress: 0,
    evidencePending: 0,
    completed: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Servicios
  const { getAgentPhoneCalls, getAgentDashboardStats } = usePhoneCallService();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Funciones para cargar datos reales (según FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md - búsqueda por texto)
  const loadPhoneCalls = async () => {
    setFilterError('');
    try {
      setLoading(true);
      if (dateFrom && dateTo && dateFrom > dateTo) {
        setFilterError('La fecha "Desde" no puede ser posterior a la fecha "Hasta".');
        return;
      }
      const filters = {
        employeeName: employeeNameFilter.trim() || undefined,
        patientName: patientNameFilter.trim() || undefined,
        companyName: companyNameFilter.trim() || undefined,
        status: statusFilter,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        scope: listScope,
      };
      const response = await getAgentPhoneCalls(page, rowsPerPage, 'callDate', 'desc', filters);
      setPhoneCalls(response.content || []);
      setTotalCount(response.totalElements || 0);
    } catch (error) {
      console.error('❌ Error loading phone calls:', error);
      setFilterError(error instanceof Error ? error.message : 'Error al cargar llamadas.');
      setPhoneCalls([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const dashboardStats = await getAgentDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Cargar datos al montar componente y cambiar página o filtros
  useEffect(() => {
    loadPhoneCalls();
  }, [page, rowsPerPage, listScope, employeeNameFilter, patientNameFilter, companyNameFilter, statusFilter, dateFrom, dateTo]);

  // Cargar estadísticas solo una vez
  useEffect(() => {
    loadStats();
  }, []);

  // Forzar que el navegador recalcule y muestre el scroll lateral tras cargar datos
  useEffect(() => {
    if (loading) return;
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
          void scrollContainerRef.current.offsetHeight;
        }
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
          void tableContainerRef.current.scrollWidth;
        }
      });
    });
    return () => cancelAnimationFrame(timer);
  }, [loading, phoneCalls.length]);

  const canOpenIntakeWizard = hasAnyRole([UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.PHONE_AGENT]);

  const handleOpenCaptureWizard = () => {
    if (!canOpenIntakeWizard) return;
    setWizardInitialCallId(null);
    setWizardReadOnly(false);
    setCaptureWizardModalOpen(true);
  };

  const handleViewCall = (call: PhoneCall) => {
    if (!call.id || !canOpenIntakeWizard) return;
    setWizardInitialCallId(call.id);
    setWizardReadOnly(true);
    setCaptureWizardModalOpen(true);
  };

  const handleEditCall = (call: PhoneCall) => {
    if (!call.id || !canOpenIntakeWizard) return;
    setWizardInitialCallId(call.id);
    setWizardReadOnly(false);
    setCaptureWizardModalOpen(true);
  };


  const handleCloseCaptureWizardModal = (refreshData = false) => {
    setCaptureWizardModalOpen(false);
    setWizardInitialCallId(null);
    setWizardReadOnly(false);
    if (refreshData) {
      loadPhoneCalls();
      loadStats();
    }
  };


  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Función para limpiar todos los filtros (FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md §7.5)
  const handleClearFilters = () => {
    setEmployeeNameFilter('');
    setPatientNameFilter('');
    setCompanyNameFilter('');
    setStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setFilterError('');
    setPage(0);
  };

  // Verificar si hay filtros activos
  const hasActiveFilters =
    employeeNameFilter !== '' ||
    patientNameFilter !== '' ||
    companyNameFilter !== '' ||
    statusFilter !== 'ALL' ||
    dateFrom !== '' ||
    dateTo !== '';

  return (
    <Box ref={scrollContainerRef} sx={{ p: 3, overflowX: 'scroll', width: '100%', minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Dashboard Agente
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Bienvenido, <strong>{user?.fullName}</strong> - Gestiona tus llamadas telefónicas y casos asignados
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canOpenIntakeWizard && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCaptureWizard}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1.5
              }}
            >
              Registrar Intake
            </Button>
          )}
        </Box>
      </Box>

      {/* Estadísticas y Filtros (colapsable, mismo estilo que Dashboard) */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={filtersExpanded ? 'Ocultar estadísticas y filtros' : 'Mostrar estadísticas y filtros'}>
            <IconButton
              size="small"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              color="primary"
            >
              {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={filtersExpanded}>
          <Box>
            {/* Estadísticas */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                Estadísticas
              </Typography>
              <Grid container spacing={2}>
                {(
                  [
                    { key: 'totalCalls', label: 'Total', color: 'primary.main', Icon: PhoneIcon },
                    { key: 'draft', label: 'Borrador', color: 'grey.600', Icon: DraftIcon },
                    { key: 'captured', label: 'Capturadas', color: 'info.main', Icon: CasesIcon },
                    { key: 'assigned', label: 'Asignadas', color: 'secondary.main', Icon: PendingIcon },
                    { key: 'inProgress', label: 'En progreso', color: 'warning.main', Icon: InProgressIcon },
                    { key: 'evidencePending', label: 'Pendiente evidencia', color: 'warning.dark', Icon: EvidenceIcon },
                    { key: 'completed', label: 'Completadas', color: 'success.main', Icon: CompletedIcon },
                  ] as const
                ).map(({ key, label, color, Icon }) => (
                  <Grid item xs={12} sm={6} md={4} lg key={key}>
                    <Card sx={{ height: '100%', borderLeft: '4px solid', borderColor: color }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Icon sx={{ fontSize: 36, color, mr: 1.5 }} />
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color }}>
                            {statsLoading ? <CircularProgress size={22} /> : stats[key]}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {label}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Filtros */}
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FilterIcon color="primary" />
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Filtros de Búsqueda
                  </Typography>
                </Box>
                {filterError && (
                  <Alert severity="error" onClose={() => setFilterError('')}>
                    {filterError}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre empleado"
                      placeholder="Ej. Juan, García"
                      value={employeeNameFilter}
                      onChange={(e) => {
                        setEmployeeNameFilter(e.target.value);
                        setPage(0);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre beneficiario"
                      placeholder="Ej. María, López"
                      value={patientNameFilter}
                      onChange={(e) => {
                        setPatientNameFilter(e.target.value);
                        setPage(0);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre empresa"
                      placeholder="Ej. Acme, Corp"
                      value={companyNameFilter}
                      onChange={(e) => {
                        setCompanyNameFilter(e.target.value);
                        setPage(0);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Estado"
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="ALL">Todos</MenuItem>
                        <MenuItem value="DRAFT">Borrador</MenuItem>
                        <MenuItem value="CAPTURED">Capturado</MenuItem>
                        <MenuItem value="ASSIGNED">Asignado</MenuItem>
                        <MenuItem value="IN_PROGRESS">En Progreso</MenuItem>
                        <MenuItem value="EVIDENCE_PENDING">Pendiente Evidencia</MenuItem>
                        <MenuItem value="COMPLETED">Completado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fecha Desde"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fecha Hasta"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                {hasActiveFilters && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Filtros activos:
                    </Typography>
                    {employeeNameFilter && (
                      <Chip
                        label={`Empleado: "${employeeNameFilter}"`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => setEmployeeNameFilter('')}
                      />
                    )}
                    {patientNameFilter && (
                      <Chip
                        label={`Paciente: "${patientNameFilter}"`}
                        size="small"
                        color="info"
                        variant="outlined"
                        onDelete={() => setPatientNameFilter('')}
                      />
                    )}
                    {companyNameFilter && (
                      <Chip
                        label={`Empresa: "${companyNameFilter}"`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onDelete={() => setCompanyNameFilter('')}
                      />
                    )}
                    {statusFilter !== 'ALL' && (
                      <Chip
                        label={`Estado: ${statusFilter}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        onDelete={() => setStatusFilter('ALL')}
                      />
                    )}
                    {(dateFrom || dateTo) && (
                      <Chip
                        label={dateFrom && dateTo ? `Fechas: ${dateFrom} - ${dateTo}` : dateFrom ? `Desde: ${dateFrom}` : `Hasta: ${dateTo}`}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          setDateFrom('');
                          setDateTo('');
                        }}
                      />
                    )}
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>
        </Collapse>
      </Box>

      {/* Tabla de Llamadas */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ px: 2, pt: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={listScope}
            onChange={(_, value: PhoneCallAgentScope) => {
              setListScope(value);
              setPage(0);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', minHeight: 44, fontWeight: 600 },
            }}
          >
            <Tab value="created" label={SCOPE_TAB_META.created.label} />
            <Tab value="assigned" label={SCOPE_TAB_META.assigned.label} />
            <Tab value="all" label={SCOPE_TAB_META.all.label} />
          </Tabs>
        </Box>
        {hasActiveFilters && (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ textTransform: 'none' }}
            >
              Limpiar Filtros
            </Button>
          </Box>
        )}
        
        <TableContainer
          ref={tableContainerRef}
          sx={{
            maxHeight: 'calc(100vh - 450px)',
            minHeight: '400px',
            overflowX: 'scroll',
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader aria-label="tabla de llamadas telefónicas" sx={{ minWidth: 1180 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Empleado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 200 }}>
                  Beneficiarios y servicios
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: 56 }}>Semáforo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  <Tooltip
                    placement="top"
                    title="Hora en que se guardó la captura en el sistema (creación o última actualización del registro). La columna «Fecha» es la fecha de la llamada."
                  >
                    <Box
                      component="span"
                      sx={{
                        cursor: 'help',
                        display: 'inline-block',
                      }}
                    >
                      Hora de captura
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  <Tooltip placement="top" title="Duración reportada de la llamada (formato HH:MM:SS cuando aplica).">
                    <Box
                      component="span"
                      sx={{
                        cursor: 'help',
                        display: 'inline-block',
                      }}
                    >
                      Duración
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Capturada por</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', maxWidth: 180 }}>
                  <Tooltip
                    placement="top"
                    title="Miembro del equipo responsable del seguimiento administrativo de la llamada o del caso. No es el especialista clínico de canalización (véase la columna «Canalización»)."
                  >
                    <Box
                      component="span"
                      sx={{
                        cursor: 'help',
                        display: 'inline-block',
                      }}
                    >
                      Usuario de seguimiento
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Crisis</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 140 }}>Canalización</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <CircularProgress size={24} /> Cargando llamadas...
                  </TableCell>
                </TableRow>
              ) : phoneCalls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    {SCOPE_TAB_META[listScope].emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                phoneCalls.map((call) => {
                  const capture = summarizeIntakeForTable(call);
                  const hasCrisis = call.hadCrisis === true || (call.crisisNotes?.trim() ?? '') !== '';
                  const creatorData = call.createdBy && typeof call.createdBy === 'object'
                    ? call.createdBy
                    : undefined;
                  const creatorFallbackId = typeof call.createdBy === 'number'
                    ? call.createdBy
                    : creatorData?.id;
                  
                  const assignedToData = call.assignedTo && typeof call.assignedTo === 'object'
                    ? call.assignedTo
                    : undefined;
                  const assignedToFallbackId = typeof call.assignedTo === 'number'
                    ? call.assignedTo
                    : assignedToData?.id;

                  return (
                    <TableRow hover key={call.id}>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {call.employee 
                          ? `${call.employee.firstName || ''} ${call.employee.lastName || ''}`.trim() || 'N/A'
                          : 'N/A'}
                      </Typography>
                      {call.employee?.middleName && (
                        <Typography variant="caption" color="text.secondary">
                          {call.employee.middleName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {capture.beneficiariesLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {capture.hintLine}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {call.employee?.accountOrgPath
                          ? call.employee.accountOrgPath
                          : call.employee?.accountId
                            ? `Account ID: ${call.employee.accountId}`
                            : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {call.callDate 
                          ? new Date(call.callDate).toLocaleDateString('es-MX', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <SeverityLevelBadge severityLevel={call.severityLevel} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatTimeEsMx(resolveCaptureInstantIso(call))}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatDurationForTable(call.duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {creatorData ? (
                        <Typography variant="body2">
                          {creatorData.fullName || creatorData.username || `ID: ${creatorData.id}`}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {creatorFallbackId ? `ID: ${creatorFallbackId}` : 'N/D'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {assignedToData ? (
                        <>
                          <Typography variant="body2">
                            {assignedToData.fullName || assignedToData.username || `ID: ${assignedToData.id}`}
                          </Typography>
                          {call.assignedToDate && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Asignado el{' '}
                              {new Date(call.assignedToDate).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                              })}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {assignedToFallbackId ? `ID: ${assignedToFallbackId}` : 'Sin usuario de seguimiento'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={getStatusLabel(call.status || 'CAPTURED')}
                        color={getStatusColor(call.status || 'CAPTURED') as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {hasCrisis ? (
                        <Chip label="Sí" size="small" color="error" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{capture.channelLine}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Ver detalles">
                          <IconButton
                            color="info"
                            onClick={() => handleViewCall(call)}
                            size="small"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar llamada">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditCall(call)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {canOpenIntakeWizard && captureWizardModalOpen && (
        <CaptureWizardModal
          open={captureWizardModalOpen}
          onClose={handleCloseCaptureWizardModal}
          initialPhoneCallId={wizardInitialCallId}
          readOnly={wizardReadOnly}
        />
      )}

    </Box>
  );
};

export default PhoneAgentDashboard;